import { useEffect, useState } from 'react';
import { db } from '../../config/firebase';
import { ref, get, update } from 'firebase/database';
import ProfilePictureUpload from './ProfilePictureUpload';

const ProfilePreviewCard = ({ user, onPhotoUpdate }) => {
  const [studentData, setStudentData] = useState(null);
  const [photoURL, setPhotoURL] = useState(user?.photoURL);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);

  useEffect(() => {
    const loadStudentData = async () => {
      try {
        const studentRef = ref(db, `students/${user.uid}`);
        const snapshot = await get(studentRef);
        const data = snapshot.val();
        if (data) {
          setStudentData(data);
        }
      } catch (error) {
        console.error('Error loading student data:', error);
      }
    };

    loadStudentData();
    setPhotoURL(user?.photoURL);
  }, [user.uid, user.photoURL]);

  const handlePhotoUpdate = async (newPhotoURL) => {
    try {
      const userRef = ref(db, `users/${user.uid}`);
      await update(userRef, { photoURL: newPhotoURL });

      setPhotoURL(newPhotoURL);
      setShowPhotoUpload(false);
      if (onPhotoUpdate) {
        onPhotoUpdate(newPhotoURL);
      }
    } catch (error) {
      console.error('Error updating photo:', error);
    }
  };

  return (
    <div className="h-full overflow-hidden rounded-[28px] border border-primary-100 bg-white shadow-sm shadow-primary-950/[0.05]">
      <div className="border-b border-stone-200 bg-stone-50 px-6 py-6 text-center">
        <button
          type="button"
          onClick={() => setShowPhotoUpload(true)}
          className="mx-auto flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-stone-100 shadow-sm"
          title="Change photo"
        >
          {photoURL ? (
            <img src={photoURL} alt="Profile" className="h-full w-full object-cover" />
          ) : (
            <span className="text-3xl font-semibold text-stone-400">
              {user.displayName?.charAt(0).toUpperCase() || '?'}
            </span>
          )}
        </button>
        <h2 className="mt-4 text-xl font-semibold text-stone-900">{user.displayName}</h2>
        <p className="text-sm capitalize text-stone-500">{user.role}</p>
      </div>

      <div className="space-y-6 px-6 py-6">
        <InfoGroup
          title="Contact"
          items={[
            { label: 'Email', value: studentData?.personalDetails?.email || user?.email },
            { label: 'Phone', value: studentData?.personalDetails?.phone || 'Add phone' },
            { label: 'Address', value: studentData?.personalDetails?.address || 'Add address' },
          ]}
        />

        <InfoGroup
          title="Personal"
          items={[
            { label: 'NIC', value: studentData?.personalDetails?.nic || 'Add NIC' },
            {
              label: 'Date of birth',
              value: studentData?.personalDetails?.dateOfBirth || 'Add birthday',
            },
          ]}
        />

        {(studentData?.education?.olSchool || studentData?.education?.alSchool) ? (
          <InfoGroup
            title="Education"
            items={[
              {
                label: 'O/L',
                value: studentData?.education?.olSchool
                  ? `${studentData.education.olSchool} (${studentData.education.olYear || 'Year'})`
                  : 'Not added',
              },
              {
                label: 'A/L',
                value: studentData?.education?.alSchool
                  ? `${studentData.education.alSchool} (${studentData.education.alYear || 'Year'})`
                  : 'Not added',
              },
            ]}
          />
        ) : null}
      </div>

      {showPhotoUpload ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/45 p-4"
          onClick={() => setShowPhotoUpload(false)}
        >
          <div
            className="w-full max-w-md rounded-[28px] bg-white p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <ProfilePictureUpload
              currentPhotoURL={photoURL}
              onPhotoUpdate={handlePhotoUpdate}
              onClose={() => setShowPhotoUpload(false)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const InfoGroup = ({ title, items }) => (
  <div>
    <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary-700">
      {title}
    </h3>
    <div className="space-y-2">
      {items.map((item) => (
        <div
          key={item.label}
          className="flex items-center justify-between gap-4 rounded-2xl bg-stone-50 px-4 py-3"
        >
          <span className="text-sm text-stone-500">{item.label}</span>
          <span className="text-sm font-semibold text-right text-stone-900">{item.value}</span>
        </div>
      ))}
    </div>
  </div>
);

export default ProfilePreviewCard;
